import React, { useMemo, useState } from 'react';
import {
  groupByDay,
  sampleAt,
  assetTimeMs,
  dirLabel,
  arrowRotation,
  swellColor,
  windColor,
  mToFt,
  kmhToKn,
  fmtFt,
  fmtKn,
  fmtPeriod,
  tideStateLabel,
  tideStateArrow,
  dayExtremes,
  localTime,
  buildManualConditions,
  tripDateList,
  dayLabel,
  sessionWindows,
  windStats,
  parseHM,
  DAY_SPEC_DEFAULTS,
} from '../utils/conditions';

const noop = () => {};

const SWELL_LABEL = ['Primary swell', 'Secondary', 'Tertiary'];

// Build the data-source tag from the conditions doc's per-variable provenance so
// it always reflects what was actually fetched: swell/wind (+ GWAM peak period)
// from Open-Meteo, tide from Stormglass or Open-Meteo's free sea-level model.
function sourceInfo(cond, preview) {
  if (preview) return { tag: 'Manual entry · preview', title: 'Live preview of manually entered conditions' };
  if (!cond) return { tag: '', title: '' };
  if (cond.manual) return { tag: 'Manual entry', title: 'Conditions entered by hand' };
  const src = cond.source || {};
  const hasPeak = !!src.swellPeak && src.swellPeak !== 'none';
  const om = ['swell', 'wind'];
  if (hasPeak) om.push('peak');
  if (src.tide === 'open-meteo') om.push('tide');
  let tag = `Open-Meteo (${om.join(', ')})`;
  if (src.tide === 'stormglass') tag += ' · Stormglass tide';
  else if (!src.tide || src.tide === 'none') tag += ' · no tide';
  const title = [
    'Swell & wind: Open-Meteo (marine + forecast)',
    hasPeak ? 'Peak period: Open-Meteo GWAM model' : 'Peak period: unavailable',
    src.tide === 'stormglass'
      ? 'Tide: Stormglass'
      : src.tide === 'open-meteo'
      ? 'Tide: Open-Meteo sea level'
      : 'Tide: none',
  ].join(' · ');
  return { tag, title };
}

// Default surf session window (spot-local) seeded for each day; fully editable.
const DEFAULT_SESSION = { start: '07:00', end: '10:30', note: '' };

// Quick clock presets for the session picker.
const SESSION_PRESETS = [
  { label: 'Dawn', start: '06:00', end: '09:00' },
  { label: 'Morning', start: '07:00', end: '10:30' },
  { label: 'Midday', start: '10:00', end: '13:00' },
  { label: 'Afternoon', start: '14:00', end: '17:00' },
  { label: 'Sunset', start: '17:00', end: '19:30' },
];

const SLIDER_MIN = 240; // 04:00
const SLIDER_MAX = 1200; // 20:00

// Swell trains present at one exact sample, ordered primary → tertiary. The
// primary train reports peak period (Surfline-style) when available.
function sampleSwells(sample) {
  return [
    { h: sample.swellH, dir: sample.swellDir, p: sample.swellPeak != null ? sample.swellPeak : sample.swellP },
    { h: sample.swell2H, dir: sample.swell2Dir, p: sample.swell2P },
    { h: sample.swell3H, dir: sample.swell3Dir, p: sample.swell3P },
  ].filter((s) => Number.isFinite(s.h) && s.h > 0.05);
}

const pad2 = (n) => String(n).padStart(2, '0');
const minToHM = (m) => `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
// Local-date 'YYYY-MM-DD' for a grouped day, using the spot's UTC offset.
function localDateKey(day, utcOffsetSec) {
  const d = new Date(day.startMs + (utcOffsetSec || 0) * 1000);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

// One session's easy clock editor: preset chips, two drag sliders over a
// dawn→dusk axis, and precise time inputs for manual tweaks. Times are spot-local.
function SessionRow({ date, index, s, onSetRange, onChange, onRemove }) {
  const startMin = parseHM(s.start);
  const endMin = parseHM(s.end);
  const setStart = (m) => {
    const mm = Math.max(SLIDER_MIN, Math.min(SLIDER_MAX, m));
    const e = endMin != null && endMin <= mm ? Math.min(SLIDER_MAX, mm + 90) : endMin;
    onSetRange(date, index, minToHM(mm), e != null ? minToHM(e) : s.end);
  };
  const setEnd = (m) => {
    const mm = Math.max(SLIDER_MIN, Math.min(SLIDER_MAX, m));
    const st = startMin != null && mm <= startMin ? Math.max(SLIDER_MIN, mm - 90) : startMin;
    onSetRange(date, index, st != null ? minToHM(st) : s.start, minToHM(mm));
  };
  const isPreset = (p) => s.start === p.start && s.end === p.end;
  return (
    <div className="cs-sess2">
      <div className="cs-sess2-top">
        <div className="cs-sess2-presets">
          {SESSION_PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              className={`cs-chip ${isPreset(p) ? 'is-on' : ''}`}
              onClick={() => onSetRange(date, index, p.start, p.end)}
              title={`${p.start}–${p.end}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <span className="cs-sess2-window">{s.start || '—'} → {s.end || '—'}</span>
        <button type="button" className="cs-tag-x" onClick={() => onRemove(date, index)} title="Remove session">×</button>
      </div>
      <div className="cs-sess2-sliders">
        <input
          type="range"
          className="cs-sess2-range"
          min={SLIDER_MIN}
          max={SLIDER_MAX}
          step={15}
          value={startMin != null ? startMin : 420}
          onChange={(e) => setStart(Number(e.target.value))}
          title="Start (drag)"
          aria-label="Session start"
        />
        <input
          type="range"
          className="cs-sess2-range"
          min={SLIDER_MIN}
          max={SLIDER_MAX}
          step={15}
          value={endMin != null ? endMin : 630}
          onChange={(e) => setEnd(Number(e.target.value))}
          title="End (drag)"
          aria-label="Session end"
        />
        <div className="cs-sess2-ticks">
          <span>4a</span><span>7a</span><span>10a</span><span>1p</span><span>4p</span><span>7p</span>
        </div>
      </div>
      <div className="cs-sess2-manual">
        <input type="time" className="cs-input cs-input-sm" value={s.start || ''} onChange={(e) => onChange(date, index, 'start', e.target.value)} title="Start (type)" />
        <span className="cs-sess-dash">→</span>
        <input type="time" className="cs-input cs-input-sm" value={s.end || ''} onChange={(e) => onChange(date, index, 'end', e.target.value)} title="End (type)" />
        <input
          type="text"
          className="cs-input cs-input-sm cs-sess-note"
          placeholder="note (e.g. dawn patrol, glassy)"
          value={s.note || ''}
          onChange={(e) => onChange(date, index, 'note', e.target.value)}
        />
      </div>
    </div>
  );
}

// Per-day surf-session editor: log when you actually surfed each day (spot-local
// time). Persisted on the trip for the conditions timeline + long-term archive.
function SessionForm({ days, value, onSetRange, onChange, onAdd, onRemove, onSave, onClose, saving }) {
  return (
    <div className="cs-manual cs-sessions">
      <div className="cs-manual-head">
        <span className="cs-help" style={{ margin: 0 }}>
          Set each day’s surf session with the clock — defaults to <strong>7:00–10:30</strong>. Wind is
          read per-hour across the session and archived with the trip.
        </span>
      </div>
      <div className="cs-manual-days">
        {days.map((date) => {
          const rows = value[date] || [];
          return (
            <div className="cs-manual-day" key={date}>
              <div className="cs-manual-day-h">
                <span>{dayLabel(date)}</span>
                <button type="button" className="cs-link" onClick={() => onAdd(date)} disabled={saving}>
                  + session
                </button>
              </div>
              {rows.length === 0 && (
                <span className="cs-help" style={{ margin: '2px 0 0' }}>No session — rest day.</span>
              )}
              {rows.map((s, i) => (
                <SessionRow
                  key={i}
                  date={date}
                  index={i}
                  s={s}
                  onSetRange={onSetRange}
                  onChange={onChange}
                  onRemove={onRemove}
                />
              ))}
            </div>
          );
        })}
      </div>
      <div className="cs-manual-actions">
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
        <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save sessions'}
        </button>
      </div>
    </div>
  );
}

// Per-day manual-entry fields. Swell & wind come first (most important); tide is
// just a daily low/high used to draw the curve.
const DAY_FIELDS = [
  { k: 'swellFt', label: 'Swell ft', step: 0.5 },
  { k: 'periodS', label: 'Period s' },
  { k: 'swellDirDeg', label: 'Swell °' },
  { k: 'windMph', label: 'Wind mph' },
  { k: 'windDirDeg', label: 'Wind °' },
  { k: 'tideLowFt', label: 'Tide low', step: 0.5 },
  { k: 'tideHighFt', label: 'Tide high', step: 0.5 },
  { k: 'lowHour', label: 'Low hr' },
  { k: 'highHour', label: 'High hr' },
];

// Manual-entry form with one editor per trip day, so swell / wind / tide can be
// set separately for each day and stay aligned to the trip's calendar.
function ManualForm({ days, onChangeDay, onApplyAll, onSave, onClose, saving }) {
  return (
    <div className="cs-manual">
      <div className="cs-manual-head">
        <span className="cs-help" style={{ margin: 0 }}>
          Enter conditions for each trip day. Swell &amp; wind are the priority; tide is set by the day’s low/high.
        </span>
        {days.length > 1 && (
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onApplyAll} disabled={saving}>
            Copy day 1 → all
          </button>
        )}
      </div>
      <div className="cs-manual-days">
        {days.map((d, i) => (
          <div className="cs-manual-day" key={d.date || i}>
            <div className="cs-manual-day-h">
              <span>{dayLabel(d.date)}</span>
              <span className="cs-manual-day-dir">{dirLabel(d.swellDirDeg)} swell · {dirLabel(d.windDirDeg)} wind</span>
            </div>
            <div className="cs-manual-grid">
              {DAY_FIELDS.map(({ k, label, step = 1 }) => (
                <label className="cs-manual-f" key={k}>
                  <span>{label}</span>
                  <input
                    type="number"
                    step={step}
                    value={d[k]}
                    onChange={(e) => onChangeDay(i, k, e.target.value === '' ? '' : Number(e.target.value))}
                  />
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="cs-manual-actions">
        <span className="cs-help" style={{ margin: 0 }}>previewing live →</span>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
        <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save to trip'}
        </button>
      </div>
    </div>
  );
}

// A small arrow pointing the way swell/wind travels — i.e. AWAY from where it
// originates (provider bearings are "coming from", so travel = +180). The glyph
// points up (north) at rotation 0: tip at the top, shaft below.
function DirArrow({ fromDeg, color, size = 26, title }) {
  if (fromDeg == null) return <span className="cs-cond-noarrow">—</span>;
  return (
    <svg className="cs-cond-arrow" width={size} height={size} viewBox="0 0 24 24" title={title}>
      <g transform={`rotate(${arrowRotation(fromDeg)} 12 12)`}>
        <path d="M12 21 L12 5 M12 5 L8 10 M12 5 L16 10" stroke={color} strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

// Tiny tide (or swell) sparkline for a single day's hours.
function Sparkline({ hours, field = 'tideM', color = '#3aa0ff', w = 132, h = 34 }) {
  const vals = hours.map((x) => x[field]).filter((v) => Number.isFinite(v));
  if (vals.length < 2) return <div className="cs-cond-spark-empty" style={{ width: w, height: h }} />;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const t0 = hours[0].t;
  const t1 = hours[hours.length - 1].t;
  const tspan = t1 - t0 || 1;
  const pts = hours
    .filter((x) => Number.isFinite(x[field]))
    .map((x) => {
      const px = ((x.t - t0) / tspan) * w;
      const py = h - 3 - ((x[field] - min) / span) * (h - 6);
      return [px, py];
    });
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;
  return (
    <svg className="cs-cond-spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <path d={area} fill={color} fillOpacity="0.14" />
      <path d={line} stroke={color} strokeWidth="1.6" fill="none" />
    </svg>
  );
}

// Per-hour wind during a surf session — small arrows (vectors) + knots so wind
// can be studied retroactively across the exact window the surfer was out. Shown
// in both the compact strip and the expanded per-day cards.
function SessionWind({ cond, hours, compact = false }) {
  if (!hours || !hours.length) return null;
  return (
    <div className={`cs-cond-swind${compact ? ' is-compact' : ''}`}>
      <div className="cs-cond-swind-h">{compact ? 'Wind / hr' : 'Session wind · hourly'}</div>
      <div className="cs-cond-swind-row">
        {hours.map((h) => {
          const kn = kmhToKn(h.windSpd);
          const g = Number.isFinite(h.windGust) ? Math.round(kmhToKn(h.windGust)) : null;
          return (
            <div
              className="cs-cond-swind-c"
              key={h.t}
              title={`${localTime(cond, h.t)} · ${fmtKn(kn)} ${dirLabel(h.windDir)}${g != null ? ` · gust ${g} kn` : ''}`}
            >
              <DirArrow fromDeg={h.windDir} color={windColor(kn)} size={compact ? 12 : 14} />
              <span className="cs-cond-swind-k">{kn == null ? '—' : Math.round(kn)}</span>
              <span className="cs-cond-swind-t">{localTime(cond, h.t).replace(/^0/, '').replace(/:00$/, '')}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Per-day summary card: dominant swell, wind and tide range.
function DayCard({ day, cond, sessionText, sessionHours }) {
  const s = day.summary;
  const ex = dayExtremes(cond, day.hours);
  const sw = sessionHours && sessionHours.length ? windStats(sessionHours) : null;
  const windDir = sw && sw.has ? sw.dir : s.windDir;
  const windMaxKn = sw && sw.has ? sw.maxKn : s.windMaxKn;
  const windMinKn = sw && sw.has ? sw.minKn : s.windMinKn;
  const hasWind = sw && sw.has ? true : s.hasWind;
  return (
    <div className="cs-cond-day">
      <div className="cs-cond-day-h">
        <span>{day.label}</span>
        {sessionText && <span className="cs-cond-sess" title="Surf session(s)">🏄 {sessionText}</span>}
      </div>

      {(s.swells && s.swells.length ? s.swells : [null]).map((train, i) => (
        <div className="cs-cond-metric" key={i}>
          <DirArrow fromDeg={train ? train.dir : null} color={swellColor(train ? train.maxFt : null)} title={`${SWELL_LABEL[i] || 'Swell'} direction`} />
          <div className="cs-cond-metric-body">
            <span className="cs-cond-metric-k">{SWELL_LABEL[i] || 'Swell'}</span>
            <span className="cs-cond-metric-v">
              {train ? `${fmtFt(train.maxFt)} @ ${fmtPeriod(train.periodAvg)}` : '—'}
            </span>
            <span className="cs-cond-metric-sub">{train ? dirLabel(train.dir) : ''}</span>
          </div>
        </div>
      ))}

      <div className="cs-cond-metric">
        <DirArrow fromDeg={windDir} color={windColor(windMaxKn)} title="Wind direction" />
        <div className="cs-cond-metric-body">
          <span className="cs-cond-metric-k">{sw && sw.has ? 'Wind · session' : 'Wind'}</span>
          <span className="cs-cond-metric-v">
            {hasWind ? `${fmtKn(windMinKn)}–${fmtKn(windMaxKn)}` : '—'}
          </span>
          <span className="cs-cond-metric-sub">{dirLabel(windDir)}</span>
        </div>
      </div>

      <SessionWind cond={cond} hours={sessionHours} />

      {s.hasTide && (
        <div className="cs-cond-tide">
          <Sparkline hours={day.hours} field="tideM" color="#3aa0ff" />
          <div className="cs-cond-tide-ex">
            {ex.length === 0 ? (
              <span className="cs-cond-metric-sub">tide {fmtFt(s.tideMinFt)}–{fmtFt(s.tideMaxFt)}</span>
            ) : (
              ex.slice(0, 4).map((e, i) => (
                <span key={i} className="cs-cond-tide-tag">
                  {e.type === 'high' ? '↑' : '↓'} {localTime(cond, e.t)} {fmtFt(e.heightFt)}
                </span>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Compact per-day chip: just swell + wind (the two that matter most), for the
// collapsed view where tide lives in the shared timeline below.
function CompactDay({ day, cond, sessionText, sessionHours }) {
  const s = day.summary;
  const sw = sessionHours && sessionHours.length ? windStats(sessionHours) : null;
  const useSess = sw && sw.has;
  const windDir = useSess ? sw.dir : s.windDir;
  const windMaxKn = useSess ? sw.maxKn : s.windMaxKn;
  const hasWind = useSess ? true : s.hasWind;
  return (
    <div className="cs-cond-cday">
      <div className="cs-cond-cday-h">
        <span className="cs-cond-cday-date">{day.label}</span>
        {sessionText && <span className="cs-cond-cday-sess" title="Surf session(s)">🏄 {sessionText}</span>}
      </div>
      {(s.swells && s.swells.length ? s.swells : [null]).map((train, i) => (
        <div className="cs-cond-cday-row" key={i}>
          <DirArrow fromDeg={train ? train.dir : null} color={swellColor(train ? train.maxFt : null)} size={14} title={SWELL_LABEL[i] || 'Swell'} />
          <span className="cs-cond-cday-v">{train ? fmtFt(train.maxFt) : '—'}</span>
          <span className="cs-cond-cday-sub">{train ? `${dirLabel(train.dir)} · ${fmtPeriod(train.periodAvg)}` : ''}</span>
        </div>
      ))}
      <div className="cs-cond-cday-row">
        <DirArrow fromDeg={windDir} color={windColor(windMaxKn)} size={14} title={useSess ? 'Wind (session)' : 'Wind direction'} />
        <span className="cs-cond-cday-v">{hasWind ? fmtKn(windMaxKn) : '—'}</span>
        <span className="cs-cond-cday-sub">{hasWind ? `${dirLabel(windDir)}${useSess ? ' · session' : ''}` : ''}</span>
      </div>
      <SessionWind cond={cond} hours={sessionHours} compact />
    </div>
  );
}

// A small "×" that clears the pinned clip/photo selection.
function CloseSel({ onClose }) {
  if (!onClose) return null;
  return (
    <button type="button" className="cs-cond-sel-x" onClick={onClose} title="Close — clear this selection" aria-label="Close selection">
      ×
    </button>
  );
}

// The exact-moment readout shown when a clip/photo is selected.
function SelectedReadout({ cond, asset, onClose }) {
  const ms = assetTimeMs(asset);
  const sample = ms != null ? sampleAt(cond, ms) : null;
  if (!sample) {
    return (
      <div className="cs-cond-sel cs-cond-sel-empty">
        <div className="cs-cond-sel-h">
          <strong>{asset.name}</strong>
          <CloseSel onClose={onClose} />
        </div>
        <span className="cs-help" style={{ margin: 0 }}>
          {ms == null
            ? 'No capture time on this asset — set one in its Details to pin conditions.'
            : 'Outside this trip’s conditions window.'}
        </span>
      </div>
    );
  }
  const windKn = kmhToKn(sample.windSpd);
  const tideFt = mToFt(sample.tideM);
  const swells = sampleSwells(sample);
  return (
    <div className="cs-cond-sel">
      <div className="cs-cond-sel-h">
        <span className="cs-cond-dot" />
        <strong>{asset.name}</strong>
        <span className="cs-cond-sel-time">{localTime(cond, sample.t)}</span>
        <CloseSel onClose={onClose} />
      </div>
      <div className="cs-cond-sel-metrics">
        {(swells.length ? swells : [null]).map((sw, i) => {
          const ft = sw ? mToFt(sw.h) : null;
          return (
            <div className="cs-cond-sel-m" key={i}>
              <DirArrow fromDeg={sw ? sw.dir : null} color={swellColor(ft)} />
              <div>
                <div className="cs-cond-metric-k">{SWELL_LABEL[i] || 'Swell'}</div>
                <div className="cs-cond-sel-mv">{sw ? `${fmtFt(ft)} @ ${fmtPeriod(sw.p)}` : '—'}</div>
                <div className="cs-cond-metric-sub">{sw ? dirLabel(sw.dir) : ''}</div>
              </div>
            </div>
          );
        })}
        <div className="cs-cond-sel-m">
          <DirArrow fromDeg={sample.windDir} color={windColor(windKn)} />
          <div>
            <div className="cs-cond-metric-k">Wind</div>
            <div className="cs-cond-sel-mv">{fmtKn(windKn)}{Number.isFinite(sample.windGust) ? ` (g${Math.round(kmhToKn(sample.windGust))})` : ''}</div>
            <div className="cs-cond-metric-sub">{dirLabel(sample.windDir)}</div>
          </div>
        </div>
        {Number.isFinite(tideFt) && (
          <div className="cs-cond-sel-m">
            <span className="cs-cond-tidearrow" style={{ color: '#3aa0ff' }}>{tideStateArrow(sample.tideState)}</span>
            <div>
              <div className="cs-cond-metric-k">Tide</div>
              <div className="cs-cond-sel-mv">{fmtFt(tideFt)}</div>
              <div className="cs-cond-metric-sub">{tideStateLabel(sample.tideState) || 'tide'}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Whole-trip hourly strip: a tide (or swell) curve with dots where each clip /
// photo was shot. Clicking a dot selects that asset.
function Timeline({ cond, assets, selected, onSelectAsset, compact = false, sessionBands = [] }) {
  const W = 1000;
  const H = compact ? 56 : 76;
  const t0 = cond.rangeStart;
  const t1 = cond.rangeEnd;
  const tspan = t1 - t0 || 1;
  const xOf = (t) => ((t - t0) / tspan) * W;

  const hasTide = cond.hours.some((h) => Number.isFinite(h.tideM));
  const field = hasTide ? 'tideM' : 'swellH';
  const vals = cond.hours.map((h) => h[field]).filter(Number.isFinite);
  const min = vals.length ? Math.min(...vals) : 0;
  const max = vals.length ? Math.max(...vals) : 1;
  const span = max - min || 1;
  const yOf = (v) => H - 6 - ((v - min) / span) * (H - 18);
  const pts = cond.hours
    .filter((h) => Number.isFinite(h[field]))
    .map((h) => `${xOf(h.t).toFixed(1)},${yOf(h[field]).toFixed(1)}`);
  const line = pts.length ? `M${pts.join(' L')}` : '';
  const area = line ? `${line} L${W},${H} L0,${H} Z` : '';

  // Day boundary ticks (local midnight).
  const days = groupByDay(cond);

  const markers = useMemo(
    () =>
      (assets || [])
        .map((a) => ({ a, ms: assetTimeMs(a) }))
        .filter((m) => m.ms != null && m.ms / 1000 >= t0 && m.ms / 1000 <= t1)
        .map((m) => ({ ...m, x: xOf(m.ms / 1000) })),
    [assets, t0, t1] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // High/low tide turning points (only meaningful when the curve is tide).
  const tides = useMemo(
    () =>
      !hasTide
        ? []
        : (cond.tideExtremes || [])
            .filter((e) => e.t >= t0 && e.t <= t1 && Number.isFinite(e.heightM))
            .map((e) => ({ ...e, x: xOf(e.t), y: yOf(e.heightM) })),
    [hasTide, cond.tideExtremes, t0, t1] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const selMs = selected ? assetTimeMs(selected) : null;
  const selX = selMs != null && selMs / 1000 >= t0 && selMs / 1000 <= t1 ? xOf(selMs / 1000) : null;

  // Surf-session windows clipped to the visible range, as shaded bands.
  const bands = (sessionBands || [])
    .map((b) => ({ ...b, x1: xOf(Math.max(b.startSec, t0)), x2: xOf(Math.min(b.endSec, t1)) }))
    .filter((b) => b.x2 > b.x1);

  return (
    <div className={`cs-cond-timeline${compact ? ' is-compact' : ''}`}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={compact ? '100%' : H}>
        {bands.map((b, i) => (
          <rect key={`sb${i}`} x={b.x1} y="0" width={b.x2 - b.x1} height={H} className="cs-cond-tl-session">
            <title>{`Surf ${b.start}–${b.end}${b.note ? ` · ${b.note}` : ''}`}</title>
          </rect>
        ))}
        {days.map((d, i) =>
          i === 0 ? null : (
            <line key={d.key} x1={xOf(d.startMs / 1000)} x2={xOf(d.startMs / 1000)} y1="0" y2={H} className="cs-cond-tl-day" />
          )
        )}
        {area && <path d={area} className="cs-cond-tl-area" />}
        {line && <path d={line} className="cs-cond-tl-line" />}
        {tides.map((e, i) => (
          <circle key={`tx${i}`} cx={e.x} cy={e.y} r="2.6" className={`cs-cond-tl-tidedot ${e.type}`} vectorEffect="non-scaling-stroke" />
        ))}
        {selX != null && <line x1={selX} x2={selX} y1="0" y2={H} className="cs-cond-tl-sel" />}
      </svg>
      {/* Tide high/low time labels along the top of the chart. */}
      <div className="cs-cond-tl-tides">
        {tides.map((e, i) => (
          <span
            key={`tl${i}`}
            className={`cs-cond-tl-tide ${e.type === 'high' ? 'is-high' : 'is-low'}`}
            style={{ left: `${(e.x / W) * 100}%` }}
          >
            {e.type === 'high' ? '▲' : '▼'} {localTime(cond, e.t)}
            <em>{fmtFt(mToFt(e.heightM))}</em>
          </span>
        ))}
      </div>
      <div className="cs-cond-tl-markers">
        {markers.map((m) => (
          <button
            key={m.a.id}
            type="button"
            className={`cs-cond-tl-dot ${m.a.kind === 'photo' ? 'is-photo' : 'is-video'} ${selected?.id === m.a.id ? 'is-sel' : ''}`}
            style={{ left: `${(m.x / W) * 100}%` }}
            title={`${m.a.name} · ${localTime(cond, m.ms / 1000)}`}
            onClick={() => onSelectAsset && onSelectAsset(m.a)}
          />
        ))}
      </div>
      <div className="cs-cond-tl-labels">
        {days.map((d) => (
          <span key={d.key} style={{ left: `${(xOf(d.startMs / 1000) / W) * 100}%` }}>{d.label}</span>
        ))}
      </div>
    </div>
  );
}

// Trip-level surf-conditions panel: swell / wind / tide for the whole trip, per
// day, and pinned to a selected clip/photo. Built from tripConditions/{id}.
export default function SurfConditions({ cond, status, assets, selected, onSelectAsset, onClearAsset, onRefresh, onSaveManual, sessions, onSaveSessions, tripStart, tripEnd }) {
  const [manualOpen, setManualOpen] = useState(false);
  const [draftDays, setDraftDays] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [draftSessions, setDraftSessions] = useState(null);
  const [savingSess, setSavingSess] = useState(false);
  // Compact by default so the overview stays as tall as the trip hero and the
  // magazine spread sits higher; expand to reveal the per-day breakdown.
  const [expanded, setExpanded] = useState(false);

  // One editor row per trip day, aligned to the trip's calendar dates.
  const tripDates = useMemo(() => tripDateList(tripStart, tripEnd), [tripStart, tripEnd]);

  const openManual = () => {
    setDraftDays(tripDates.map((date) => ({ date, ...DAY_SPEC_DEFAULTS })));
    setManualOpen(true);
  };
  const closeManual = () => {
    setManualOpen(false);
    setDraftDays(null);
  };
  const changeDay = (i, k, v) =>
    setDraftDays((prev) => (prev ? prev.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)) : prev));
  const applyAll = () =>
    setDraftDays((prev) => (prev ? prev.map((d) => ({ ...prev[0], date: d.date })) : prev));

  // Surf-session editor (seeded from saved sessions, else the 7:00–10:30 default
  // per day so it's one click to accept or drag to adjust).
  const openSessions = () => {
    const init = {};
    tripDates.forEach((date) => {
      const existing = (sessions && sessions[date]) || [];
      init[date] = existing.length ? existing.map((s) => ({ ...s })) : [{ ...DEFAULT_SESSION }];
    });
    setDraftSessions(init);
    setSessionsOpen(true);
  };
  const closeSessions = () => {
    setSessionsOpen(false);
    setDraftSessions(null);
  };
  const changeSession = (date, i, k, v) =>
    setDraftSessions((prev) =>
      prev ? { ...prev, [date]: prev[date].map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) } : prev
    );
  const setSessionRange = (date, i, start, end) =>
    setDraftSessions((prev) =>
      prev ? { ...prev, [date]: prev[date].map((s, idx) => (idx === i ? { ...s, start, end } : s)) } : prev
    );
  const addSession = (date) =>
    setDraftSessions((prev) => (prev ? { ...prev, [date]: [...(prev[date] || []), { ...DEFAULT_SESSION }] } : prev));
  const removeSession = (date, i) =>
    setDraftSessions((prev) => (prev ? { ...prev, [date]: prev[date].filter((_, idx) => idx !== i) } : prev));

  // Live preview built from the manual form (no persistence until "Save").
  const preview = useMemo(
    () => (manualOpen && draftDays ? buildManualConditions({ days: draftDays }) : null),
    [manualOpen, draftDays]
  );

  const active = preview || cond;
  const days = useMemo(() => (active ? groupByDay(active) : []), [active]);
  const ready = active && active.status === 'ready' && active.hours?.length;
  const busy = status === 'queued' || status === 'fetching';

  // Surf-session windows on the timeline + a short per-day label.
  const bands = useMemo(
    () => (ready ? sessionWindows(sessions, active.utcOffsetSec) : []),
    [ready, sessions, active]
  );
  const sessionByKey = useMemo(() => {
    const m = {};
    if (!ready || !sessions) return m;
    days.forEach((d) => {
      const list = sessions[localDateKey(d, active.utcOffsetSec)];
      if (list && list.length) m[d.key] = list.map((s) => `${s.start}–${s.end}`).join(', ');
    });
    return m;
  }, [ready, sessions, days, active]);

  // The conditions hours that fall inside each day's session window(s), so the
  // day's wind reflects when they actually surfed (and supports per-hour study).
  const sessionHoursByKey = useMemo(() => {
    const m = {};
    if (!ready || !bands.length) return m;
    days.forEach((d) => {
      const key = localDateKey(d, active.utcOffsetSec);
      const dayBands = bands.filter((b) => b.date === key);
      if (!dayBands.length) return;
      const hrs = active.hours.filter((h) =>
        dayBands.some((b) => h.t >= Math.floor(b.startSec) - 1 && h.t <= Math.ceil(b.endSec) + 1)
      );
      if (hrs.length) m[d.key] = hrs;
    });
    return m;
  }, [ready, bands, days, active]);

  const { tag: sourceLabel, title: sourceTitle } = sourceInfo(cond, preview);

  const save = async () => {
    if (!onSaveManual || !draftDays) return;
    const built = buildManualConditions({ days: draftDays });
    if (!built) return;
    setSaving(true);
    try {
      await onSaveManual(built);
      closeManual();
    } finally {
      setSaving(false);
    }
  };

  const saveSessions = async () => {
    if (!onSaveSessions || !draftSessions) return;
    setSavingSess(true);
    try {
      await onSaveSessions(draftSessions);
      closeSessions();
    } finally {
      setSavingSess(false);
    }
  };

  return (
    <section className="cs-cond-wrap">
      <div className="cs-cond-head">
        <h2 className="cs-cond-title">🌊 Surf conditions</h2>
        {sourceLabel && <span className="cs-cond-src" title={sourceTitle}>{sourceLabel}</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {busy && <span className="cs-help" style={{ margin: 0 }}>fetching…</span>}
          {ready && (
            <button
              className={`cs-btn cs-btn-sm ${expanded ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={() => setExpanded((v) => !v)}
              title={expanded ? 'Compact view' : 'Expand — show the per-day breakdown'}
            >
              {expanded ? '⤡ Compact' : '⤢ Expand'}
            </button>
          )}
          {onSaveSessions && (
            <button
              className={`cs-btn cs-btn-sm ${sessionsOpen ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={() => (sessionsOpen ? closeSessions() : openSessions())}
              title="Log when you surfed each day (archived & marked on the timeline)"
            >
              🏄 Sessions
            </button>
          )}
          {onSaveManual && (
            <button
              className={`cs-btn cs-btn-sm ${manualOpen ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={() => (manualOpen ? closeManual() : openManual())}
              title="Enter swell, wind & tide by hand, per day"
            >
              ✎ Manual
            </button>
          )}
          {onRefresh && (
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onRefresh} disabled={busy}>
              ↻ Refresh
            </button>
          )}
        </span>
      </div>

      {manualOpen && draftDays && (
        <ManualForm
          days={draftDays}
          onChangeDay={changeDay}
          onApplyAll={applyAll}
          onSave={save}
          onClose={closeManual}
          saving={saving}
        />
      )}

      {sessionsOpen && draftSessions && (
        <SessionForm
          days={tripDates}
          value={draftSessions}
          onSetRange={setSessionRange}
          onChange={changeSession}
          onAdd={addSession}
          onRemove={removeSession}
          onSave={saveSessions}
          onClose={closeSessions}
          saving={savingSess}
        />
      )}

      {!ready ? (
        <div className="cs-cond-state">
          {busy ? (
            <span>Pulling swell, wind and tide for this spot…</span>
          ) : status === 'skipped' ? (
            <span>Add a location and trip dates (Edit trip) to pull surf conditions, or ✎ enter them manually.</span>
          ) : status === 'error' ? (
            <span>Couldn’t load conditions. <button className="cs-link" onClick={onRefresh}>Try again</button></span>
          ) : (
            <span>
              No conditions yet.{' '}
              {onRefresh && <button className="cs-link" onClick={onRefresh}>Fetch swell, wind &amp; tide</button>}
              {onSaveManual && <> · or <button className="cs-link" onClick={openManual}>enter manually</button></>}
            </span>
          )}
        </div>
      ) : (
        <>
          {selected && !preview && (
            <SelectedReadout cond={active} asset={selected} onClose={onClearAsset || noop} />
          )}

          {expanded || preview ? (
            <>
              <Timeline cond={active} assets={assets} selected={preview ? null : selected} onSelectAsset={onSelectAsset} sessionBands={preview ? [] : bands} />
              <div className="cs-cond-days">
                {days.map((d) => (
                  <DayCard
                    key={d.key}
                    day={d}
                    cond={active}
                    sessionText={sessionByKey[d.key]}
                    sessionHours={sessionHoursByKey[d.key]}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Compact: swell + wind per day up top, the shared tide curve below. */}
              <div className="cs-cond-cstrip">
                {days.map((d) => (
                  <CompactDay
                    key={d.key}
                    day={d}
                    cond={active}
                    sessionText={sessionByKey[d.key]}
                    sessionHours={sessionHoursByKey[d.key]}
                  />
                ))}
              </div>
              <Timeline cond={active} assets={assets} selected={selected} onSelectAsset={onSelectAsset} compact sessionBands={bands} />
            </>
          )}

          {active.source?.tide === 'none' && (
            <div className="cs-help" style={{ marginTop: 8 }}>
              Tide data unavailable (no tide provider configured). Showing swell &amp; wind.
            </div>
          )}
        </>
      )}
    </section>
  );
}
